/**
 * Phase F1 — Resolved notifications / Display Preferences timezone for trade period queries.
 * Refetches when the user signs in or saves timezone in Display Preferences
 * (`displayPreferencesChanged` custom event).
 */

import { useAuth } from "@clerk/clerk-react";
import { useCallback, useEffect, useState } from "react";
import {
  fetchNotificationTimezone,
  NotificationTimezoneResponse,
} from "../services/notificationsApi";
import { deviceIanaTimeZone, resolveUserReportingTimeZone } from "../utils/reportingTimeZone";
import { getDisplayTimezoneMode, writeCachedTimezoneSource } from "../utils/displayTimezoneMode";

/** Must match `DisplayPreferences` / profile settings. */
export const DISPLAY_PREFERENCES_CHANGED_EVENT = "displayPreferencesChanged";

export interface UseUserNotificationTimezoneResult {
  /** Resolved IANA string from API, or null before load / when signed out */
  timezone: string | null;
  timezoneSource: NotificationTimezoneResponse["timezone_source"] | null;
  isLoading: boolean;
  error: Error | null;
  /** Incremented after each successful load — use as a dependency to refetch trade data */
  revision: number;
  refetch: () => Promise<void>;
}

export function useUserNotificationTimezone(): UseUserNotificationTimezoneResult {
  const { isLoaded, isSignedIn } = useAuth();
  const [timezone, setTimezone] = useState<string | null>(null);
  const [timezoneSource, setTimezoneSource] = useState<
    NotificationTimezoneResponse["timezone_source"] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [revision, setRevision] = useState(0);

  const load = useCallback(async () => {
    if (!isLoaded || !isSignedIn) {
      setTimezone(null);
      setTimezoneSource(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchNotificationTimezone();
      const deviceZone = deviceIanaTimeZone();
      const mode = getDisplayTimezoneMode(res, deviceZone);
      setTimezone(resolveUserReportingTimeZone(res.timezone, mode));
      setTimezoneSource(mode);
      setRevision((r) => r + 1);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      setTimezone(deviceIanaTimeZone());
      setTimezoneSource("auto");
      setRevision((r) => r + 1);
    } finally {
      setIsLoading(false);
    }
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onPrefs = (ev: Event) => {
      const detail = (ev as CustomEvent<{ setting?: string }>).detail;
      if (detail?.setting === "timezone") {
        void load();
      }
    };
    window.addEventListener(DISPLAY_PREFERENCES_CHANGED_EVENT, onPrefs as EventListener);
    return () =>
      window.removeEventListener(DISPLAY_PREFERENCES_CHANGED_EVENT, onPrefs as EventListener);
  }, [load]);

  return {
    timezone,
    timezoneSource,
    isLoading,
    error,
    revision,
    refetch: load,
  };
}
